import React, { useState } from 'react';
import { X, User, Mail, Phone, Briefcase, DollarSign, Calendar, CheckCircle2 } from 'lucide-react';

const ROLES = [
  { value: 'employee', label: 'Employee', description: 'Regular staff member with limited access' },
  { value: 'admin', label: 'Admin', description: 'Full access to manage the organization' },
];

const HireApplicantModal = ({ isOpen, onClose, onSubmit, applicant, isLoading }) => {
  const [formData, setFormData] = useState({
    role: 'employee',
    title: '',
    department: '',
    startDate: '',
    salary: '',
    employmentType: 'full_time',
    sendWelcomeEmail: true,
    generateLoginCredentials: true,
    notes: '',
  });

  const [step, setStep] = useState(1);

  if (!isOpen || !applicant) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit({
      ...formData,
      salary: formData.salary ? parseFloat(formData.salary) : null,
    });
  };

  const handleClose = () => {
    setFormData({
      role: 'employee',
      title: '',
      department: '',
      startDate: '',
      salary: '',
      employmentType: 'full_time',
      sendWelcomeEmail: true,
      generateLoginCredentials: true,
      notes: '',
    });
    setStep(1);
    onClose();
  };

  const canProceed = () => {
    if (step === 1) {
      return formData.role && formData.title;
    }
    return true;
  };

  const renderStepIndicator = () => (
    <div className="flex items-center justify-center mb-6">
      <div className="flex items-center">
        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-medium ${
          step >= 1 ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-500'
        }`}>
          1
        </div>
        <div className={`w-16 h-1 ${step >= 2 ? 'bg-purple-600' : 'bg-gray-200'}`} />
        <div className={`w-8 h-8 rounded-full flex items-center justify-center font-medium ${
          step >= 2 ? 'bg-purple-600 text-white' : 'bg-gray-200 text-gray-500'
        }`}>
          2
        </div>
      </div>
    </div>
  );

  const renderStep1 = () => (
    <div className="space-y-4">
      {/* Applicant Summary */}
      <div className="bg-purple-50 rounded-lg p-4 mb-6">
        <h4 className="font-medium text-purple-900 mb-2">Applicant Information</h4>
        <div className="space-y-1 text-sm">
          <p className="flex items-center text-gray-700">
            <User size={14} className="mr-2 text-purple-600" />
            {applicant.firstName} {applicant.lastName}
          </p>
          <p className="flex items-center text-gray-700">
            <Mail size={14} className="mr-2 text-purple-600" />
            {applicant.email}
          </p>
          {applicant.phone && (
            <p className="flex items-center text-gray-700">
              <Phone size={14} className="mr-2 text-purple-600" />
              {applicant.phone}
            </p>
          )}
        </div>
      </div>

      {/* Role Selection */}
      <div>
        <label className="form-label">Role</label>
        <div className="space-y-3 mt-2">
          {ROLES.map((role) => (
            <label
              key={role.value}
              className={`flex items-start p-3 rounded-lg border cursor-pointer transition-all ${
                formData.role === role.value
                  ? 'border-purple-500 bg-purple-50'
                  : 'border-gray-200 hover:border-purple-300'
              }`}
            >
              <input
                type="radio"
                name="role"
                value={role.value}
                checked={formData.role === role.value}
                onChange={(e) => setFormData({ ...formData, role: e.target.value })}
                className="mt-1 w-4 h-4 text-purple-600"
              />
              <div className="ml-3">
                <span className="font-medium text-gray-900">{role.label}</span>
                <p className="text-sm text-gray-500">{role.description}</p>
              </div>
            </label>
          ))}
        </div>
      </div>

      {/* Job Title */}
      <div>
        <label className="form-label">Job Title *</label>
        <div className="relative">
          <Briefcase className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="text"
            className="form-input pl-10"
            placeholder="e.g., Math Teacher, Office Manager"
            value={formData.title}
            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
            required
          />
        </div>
      </div>

      {/* Department */}
      <div>
        <label className="form-label">Department</label>
        <input
          type="text"
          className="form-input"
          placeholder="e.g., Teaching Staff, Administration"
          value={formData.department}
          onChange={(e) => setFormData({ ...formData, department: e.target.value })}
        />
      </div>
    </div>
  );

  const renderStep2 = () => (
    <div className="space-y-4">
      {/* Employment Type */}
      <div>
        <label className="form-label">Employment Type</label>
        <select
          className="form-input"
          value={formData.employmentType}
          onChange={(e) => setFormData({ ...formData, employmentType: e.target.value })}
        >
          <option value="full_time">Full Time</option>
          <option value="part_time">Part Time</option>
          <option value="contract">Contract</option>
          <option value="intern">Intern</option>
        </select>
      </div>

      {/* Start Date */}
      <div>
        <label className="form-label">Start Date</label>
        <div className="relative">
          <Calendar className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="date"
            className="form-input pl-10"
            value={formData.startDate}
            onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
          />
        </div>
      </div>

      {/* Salary */}
      <div>
        <label className="form-label">Salary</label>
        <div className="relative">
          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
          <input
            type="number"
            className="form-input pl-10"
            placeholder="Annual salary amount"
            value={formData.salary}
            onChange={(e) => setFormData({ ...formData, salary: e.target.value })}
          />
        </div>
      </div>

      {/* Options */}
      <div className="space-y-3 pt-4 border-t">
        <label className="flex items-center space-x-3">
          <input
            type="checkbox"
            checked={formData.sendWelcomeEmail}
            onChange={(e) => setFormData({ ...formData, sendWelcomeEmail: e.target.checked })}
            className="w-4 h-4 text-purple-600 rounded"
          />
          <span className="text-gray-700">Send welcome email</span>
        </label>
        <label className="flex items-center space-x-3">
          <input
            type="checkbox"
            checked={formData.generateLoginCredentials}
            onChange={(e) => setFormData({ ...formData, generateLoginCredentials: e.target.checked })}
            className="w-4 h-4 text-purple-600 rounded"
          />
          <span className="text-gray-700">Generate login credentials</span>
        </label>
      </div>

      {/* Notes */}
      <div>
        <label className="form-label">Additional Notes</label>
        <textarea
          className="form-input"
          rows="3"
          placeholder="Any additional notes about the hire..."
          value={formData.notes}
          onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
        />
      </div>
    </div>
  );

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Hire Applicant</h2>
            <p className="text-sm text-gray-500 mt-1">
              Convert {applicant.firstName} {applicant.lastName} to an employee
            </p>
          </div>
          <button onClick={handleClose} className="text-gray-400 hover:text-gray-600">
            <X size={24} />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {renderStepIndicator()}
          <form id="hire-form" onSubmit={handleSubmit}>
            {step === 1 ? renderStep1() : renderStep2()}
          </form>
        </div>

        {/* Footer */}
        <div className="flex justify-between space-x-3 p-6 border-t border-gray-100">
          {step === 1 ? (
            <>
              <button
                type="button"
                onClick={handleClose}
                className="btn-secondary"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => setStep(2)}
                disabled={!canProceed()}
                className="btn-primary disabled:opacity-50"
              >
                Continue
              </button>
            </>
          ) : (
            <>
              <button
                type="button"
                onClick={() => setStep(1)}
                className="btn-secondary"
              >
                Back
              </button>
              <button
                type="submit"
                form="hire-form"
                disabled={isLoading}
                className="btn-primary disabled:opacity-50 flex items-center space-x-2"
              >
                <CheckCircle2 size={18} />
                <span>{isLoading ? 'Hiring...' : 'Complete Hire'}</span>
              </button>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default HireApplicantModal;
